import { type FunctionComponent } from "react";
import { useQuery } from "@tanstack/react-query";
import { Alert, Descriptions, Drawer, Empty, Skeleton, Tag, Timeline, Typography } from "antd";
import dayjs from "dayjs";

import { CloudFunctions } from "@fitness-app/data-models/http/registry";

import { callFn } from "@fitness-app/app-store/src/helpers/callFn";

import { traineeKeys } from "~/modules/Trainee/TraineeProfile/scope";
import { traineeApi } from "~/store/initializeStore";
import { emailEventColors, emailEventLabels, emailTemplateLabels } from "./emailLabels";

type Props = {
  traineeId: string;
  messageId: string | null;
  onClose: () => void;
};

const SentEmailPreviewDrawer: FunctionComponent<Props> = ({ traineeId, messageId, onClose }) => {
  const emailQuery = useQuery({
    queryKey: [...traineeKeys.all(traineeId), "sentEmail", messageId],
    enabled: Boolean(messageId),
    queryFn: async () => {
      const { data } = await callFn(traineeApi, CloudFunctions.trainee.routes.getSentEmail, undefined, {
        params: { traineeId, messageId: messageId as string },
      });

      return data;
    },
  });

  const email = emailQuery.data;

  return (
    <Drawer open={Boolean(messageId)} onClose={onClose} width={860} title={email?.subject || "Podgląd maila"}>
      {emailQuery.isFetching && <Skeleton active paragraph={{ rows: 6 }} />}

      {emailQuery.isError && (
        <Alert type="error" showIcon message="Nie udało się pobrać treści maila" style={{ marginBottom: 16 }} />
      )}

      {email && !emailQuery.isFetching && (
        <>
          <Descriptions column={1} size="small" bordered style={{ marginBottom: 24 }}>
            <Descriptions.Item label="Do">{email.recipient}</Descriptions.Item>
            <Descriptions.Item label="Od">{email.from}</Descriptions.Item>
            <Descriptions.Item label="Wysłany">{dayjs(email.sentAt).format("DD.MM.YYYY HH:mm:ss")}</Descriptions.Item>
            <Descriptions.Item label="Rodzaj">
              {email.tag ? <Tag>{emailTemplateLabels[email.tag] ?? email.tag}</Tag> : "-"}
            </Descriptions.Item>
          </Descriptions>

          {email.events.length > 0 && (
            <>
              <Typography.Title level={5}>Co się z nim działo</Typography.Title>
              <Timeline
                style={{ marginBottom: 24 }}
                items={email.events.map((event) => ({
                  color: emailEventColors[event.type] ?? "gray",
                  children: `${dayjs(event.receivedAt).format("DD.MM.YYYY HH:mm")} - ${
                    emailEventLabels[event.type] ?? event.type
                  }`,
                }))}
              />
            </>
          )}

          <Typography.Title level={5}>Treść</Typography.Title>
          {email.htmlBody ? (
            // Treść maila to obcy HTML (szablony Postmarka, linki, obrazki). Trzymamy
            // go w sandboksowanej ramce zamiast wstrzykiwać w DOM panelu — inaczej
            // dowolny szablon mógłby wykonać skrypt w kontekście admina.
            <iframe
              title="Podgląd maila"
              sandbox=""
              srcDoc={email.htmlBody}
              style={{ width: "100%", height: 600, border: "1px solid #f0f0f0", borderRadius: 8, background: "#fff" }}
            />
          ) : email.textBody ? (
            <Typography.Paragraph style={{ whiteSpace: "pre-wrap" }}>{email.textBody}</Typography.Paragraph>
          ) : (
            <Empty description="Postmark nie zwrócił treści tego maila" />
          )}
        </>
      )}
    </Drawer>
  );
};

export default SentEmailPreviewDrawer;
